// @ts-check
import React from 'react';
import { useQueryParams } from 'use-query-params';
import { useSpring, animated } from 'react-spring';
import { useMeasure } from 'react-use';
import { useMediaQuery } from 'react-responsive';

import '../typedefs';
import { getUpcomingConversations } from '../api';
import { dateFilterOptions } from '../conversation-filter-options';
import { CommaArrayParam, generateFilters } from '../utils';
import { mobileMaxWidth } from '../constants';

import ConversationCards from '../components/ConversationCards';
import ConversationFilter from '../components/ConversationFilter';
import Link from '../components/Link';
import LoadingSpinner from '../components/LoadingSpinner';

const defaultContentHeight = '750px';

const Conversations = ({ shiftType = 'participant' }) => {
  /**
   * @type {[Shift[], Function]} response from API with calendar data
   */
  const [conversations, setConversations] = React.useState([]);
  const [filteredConversations, setFilteredConversations] = React.useState(
    conversations
  );
  const [allFilterOptions, setAllFilterOptions] = React.useState(undefined);
  const [isLoading, setIsLoading] = React.useState(true);
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const [query, setQuery] = useQueryParams({
    date: CommaArrayParam,
    topic: CommaArrayParam,
    language: CommaArrayParam,
    partner: CommaArrayParam,
    location: CommaArrayParam,
  });

  // fetch conversations from Leaven
  React.useEffect(() => {
    const params = { shift_type: shiftType };
    getUpcomingConversations(params).then((d) => {
      setConversations(d.shifts);
      setAllFilterOptions({
        date: dateFilterOptions,
        language: generateFilters(d.languages, query.language),
        partner: generateFilters(d.partners, query.partner),
        topic: generateFilters(d.topics, query.topic),
        location: generateFilters(d.locations, query.location),
      });
      setIsLoading(false);
    });
  }, [setConversations, setIsLoading, getUpcomingConversations]);

  // handle animations for content size changes
  const [contentHeight, setContentHeight] = React.useState(
    defaultContentHeight
  );
  const [ref, { height }] = useMeasure();
  React.useEffect(() => {
    setContentHeight(height);
    // adds resize event listener
    window.addEventListener('resize', setContentHeight(height));
    // clean-up
    return window.removeEventListener('resize', setContentHeight(height));
  }, [height]);
  // useMeasure depends on ResizeObserver to get contentHeight used in animated props
  const [supportsHeightAnimation, setSupportsHeightAnimation] = React.useState(
    false
  );
  React.useEffect(() => {
    setSupportsHeightAnimation(Boolean(window.ResizeObserver));
  }, []);

  const showEmptyState = filteredConversations.length === 0;
  const animatedProps = useSpring({
    height: showEmptyState ? defaultContentHeight : `${contentHeight}px`,
  });

  // active dropdown options
  const [activeDateOptions, setActiveDateOptions] = React.useState([]);
  const [activeTopicOptions, setActiveTopicOptions] = React.useState([]);
  const [activeLanguageOptions, setActiveLanguageOptions] = React.useState([]);
  const [activePartnerOptions, setActivePartnerOptions] = React.useState([]);
  const [activeLocationOptions, setActiveLocationOptions] = React.useState([]);

  // update filtered conversations when active filter options change
  React.useEffect(() => {
    setFilteredConversations(conversations.filter(showConversation));
  }, [
    conversations,
    activeDateOptions,
    activeTopicOptions,
    activeLanguageOptions,
    activePartnerOptions,
    activeLocationOptions,
  ]);

  function showConversation(c) {
    if (activeDateOptions.length) {
      const isWithinDateRange = activeDateOptions.some(
        (option) => option.range && option.range.contains(c.start_datetime)
      );
      if (!isWithinDateRange) {
        return false;
      }
    }

    if (activeTopicOptions.length) {
      const hasTopic = activeTopicOptions.some((option) =>
        c.job.topics.includes(option.display)
      );
      if (!hasTopic) {
        return false;
      }
    }

    if (activeLanguageOptions.length) {
      const hasLanguage = activeLanguageOptions.some(
        (option) => c.job.language === option.display
      );
      if (!hasLanguage) {
        return false;
      }
    }

    if (activePartnerOptions.length) {
      const hasPartner = activePartnerOptions.some(
        (option) => c.job.campaign.account.name === option.display
      );
      if (!hasPartner) {
        return false;
      }
    }

    if (activeLocationOptions.length) {
      const hasLocation = activeLocationOptions.some(
        (option) => c.job.city_and_state === option.display
      );
      if (!hasLocation) {
        return false;
      }
    }

    return true;
  }

  const handleClearAllFilters = () => {
    setQuery(
      {
        date: undefined,
        topic: undefined,
        language: undefined,
        partner: undefined,
        location: undefined,
      },
      'pushIn'
    );
  };

  return (
    <animated.div style={supportsHeightAnimation ? animatedProps : {}}>
      <div ref={ref}>
        {isLoading || !allFilterOptions ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={
              supportsHeightAnimation ? { height: defaultContentHeight } : {}
            }
          >
            <LoadingSpinner size="md" active={isLoading} />
          </div>
        ) : (
          <>
            <p>
              Have questions? Learn more about LVN conversations{' '}
              <Link to="https://help.lvn.org/hc/en-us/categories/360005919073-Cortico-FAQ">
                here
              </Link>
              .
            </p>
            <div>
              <div
                className="filter-container d-flex mb-2"
                data-testid="filter-toggles"
              >
                <ConversationFilter
                  title="Location"
                  filterOptions={allFilterOptions.location}
                  activeOptions={activeLocationOptions}
                  setActiveOptions={setActiveLocationOptions}
                  isMobile={isMobile}
                />
                <ConversationFilter
                  title="Topic"
                  filterOptions={allFilterOptions.topic}
                  activeOptions={activeTopicOptions}
                  setActiveOptions={setActiveTopicOptions}
                  isMobile={isMobile}
                />
                <ConversationFilter
                  title="Date"
                  filterOptions={allFilterOptions.date}
                  activeOptions={activeDateOptions}
                  setActiveOptions={setActiveDateOptions}
                  isMobile={isMobile}
                />
                <ConversationFilter
                  title="Language"
                  filterOptions={allFilterOptions.language}
                  activeOptions={activeLanguageOptions}
                  setActiveOptions={setActiveLanguageOptions}
                  isMobile={isMobile}
                />
                <ConversationFilter
                  title="Partner"
                  filterOptions={allFilterOptions.partner}
                  activeOptions={activePartnerOptions}
                  setActiveOptions={setActivePartnerOptions}
                  isMobile={isMobile}
                />
              </div>
              <ConversationCards
                conversations={filteredConversations}
                showEmptyState={showEmptyState}
                filtered={conversations.length !== 0}
                onClearAllFilters={handleClearAllFilters}
              />
            </div>
          </>
        )}
      </div>
    </animated.div>
  );
};

export default Conversations;
